import { requestBuilder } from '../../http/RequestBuilder';
import type {
    AdminBridgeftReportResponseDto,
    AdminResetBridgeftHouseholdsResponseDto,
    AdminSyncPerformanceResponseDto,
    ApexPerformanceByHorizonResponse,
} from '../types/BridgeftTypes';

interface BridgeftPerformanceParams {
    accountNumber?: string;
    householdId?: string;
}

/**
 * Get normalized performance data (same as what member sees) for an account or household.
 * Accepts accountNumber or householdId as query params.
 * @example GET /api/admin/investments/bridgeft-performance?accountNumber=...
 * @example GET /api/admin/investments/bridgeft-performance?householdId=...
 */
export const useGetBridgeftPerformance = requestBuilder.query<
    ApexPerformanceByHorizonResponse,
    BridgeftPerformanceParams
>({
    endpoint: ({ accountNumber, householdId }) => {
        const params = new URLSearchParams();
        if (householdId != null && householdId.length > 0)
            params.set('householdId', householdId);
        else if (accountNumber != null && accountNumber.length > 0)
            params.set('accountNumber', accountNumber);
        return `/api/admin/investments/bridgeft-performance?${params.toString()}`;
    },
    queryKey: ({ accountNumber, householdId }) =>
        `/api/admin/investments/bridgeft-performance/${householdId ?? accountNumber}`,
});

interface BridgeftReportParams {
    accountNumber?: string;
    householdId?: string;
    subReports: string;
    startDate: string;
    endDate: string;
    searchKey: number;
}

/**
 * Get BridgeFT sub-report data. Supports lookup by accountNumber, accountId, or householdId.
 * @example GET /api/admin/investments/bridgeft-report?accountNumber=...&subReports=...
 * @example GET /api/admin/investments/bridgeft-report?householdId=...&subReports=...
 */
export const useGetBridgeftReport = requestBuilder.query<
    AdminBridgeftReportResponseDto,
    BridgeftReportParams
>({
    endpoint: ({
        accountNumber,
        householdId,
        subReports,
        startDate,
        endDate,
    }) => {
        const params = new URLSearchParams();
        if (householdId != null && householdId.length > 0)
            params.set('householdId', householdId);
        else if (accountNumber != null && accountNumber.length > 0)
            params.set('accountNumber', accountNumber);
        params.set('subReports', subReports);
        params.set('startDate', startDate);
        params.set('endDate', endDate);
        return `/api/admin/investments/bridgeft-report?${params.toString()}`;
    },
    queryKey: ({
        accountNumber,
        householdId,
        subReports,
        startDate,
        endDate,
        searchKey,
    }) =>
        `/api/admin/investments/bridgeft-report/${householdId ?? accountNumber}/${subReports}/${startDate}/${endDate}/${searchKey}`,
});

/**
 * Starts the nightly BridgeFT identity sync + performance cache workflow.
 * Returns as soon as the durable workflow is queued; watch #im-ops or the
 * DBOS Dashboard tab for results.
 * @example POST /api/admin/investments/sync-performance
 */
export const useSyncBridgeftPerformance = requestBuilder.mutation<
    AdminSyncPerformanceResponseDto,
    undefined
>({
    endpoint: '/api/admin/investments/sync-performance',
    method: 'POST',
});

/**
 * Local-only. Deletes BridgeFT households linked to memberships and clears
 * local household / account ids plus performance caches.
 * @example POST /api/admin/investments/reset-bridgeft-households
 */
export const useResetBridgeftHouseholds = requestBuilder.mutation<
    AdminResetBridgeftHouseholdsResponseDto,
    undefined
>({
    endpoint: '/api/admin/investments/reset-bridgeft-households',
    method: 'POST',
});
